import React from "react";
import { motion } from "framer-motion";
import { Briefcase, Lightbulb, Globe2 } from "lucide-react";
import logo from "../assets/blackbear.png";

const About = () => {
  return (
    <section className="min-h-screen bg-[#f5f5f5] dark:bg-slate-900 text-[#111] dark:text-white px-4 sm:px-6 pt-24 sm:pt-32 pb-16 font-[Inter] transition-colors">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true }}
        className="max-w-5xl mx-auto text-center"
      >
        {/* Logo + Heading */}
        <motion.img
          src={logo}
          alt="Blackbear Logo"
          className="w-28 h-28 sm:w-36 sm:h-36 mx-auto mb-4"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        />
        <h1 className="text-3xl md:text-4xl font-[DM Sans] font-extrabold mb-4">
          About Blackbear Technologies
        </h1>
        <p className="text-base sm:text-lg text-gray-600 dark:text-slate-300 max-w-3xl mx-auto mb-10">
          Blackbear Technologies blends creativity with code to craft AI-driven tools, sleek web apps, and smart ERP systems. We may be local, but our solutions are designed to go global — transforming the way businesses think, work, and grow.
        </p>
      </motion.div>

      {/* Highlights Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-center mt-10">
        {[
          {
            icon: <Briefcase size={28} className="mx-auto mb-4 text-black dark:text-white" />,
            title: "Built on Experience",
            text: "Our team brings years of expertise in supply chain, SAP, software development, and intelligent automation."
          },
          {
            icon: <Lightbulb size={28} className="mx-auto mb-4 text-black dark:text-white" />,
            title: "Innovation First",
            text: "We use modern frameworks, GPT-4, and Mistral LLMs to build tools that think, automate, and enhance operations."
          },
          {
            icon: <Globe2 size={28} className="mx-auto mb-4 text-black dark:text-white" />,
            title: "Boundless by Design",
            text: "We craft solutions meant to travel. From local roots to limitless horizons, our technology is designed to scale, connect, and evolve with businesses wherever opportunity takes them."
          }
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.2 }}
            viewport={{ once: true }}
            className="p-5 sm:p-6 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-all"
          >
            {item.icon}
            <h3 className="font-[DM Sans] font-bold text-base sm:text-lg mb-2">
              {item.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-300">
              {item.text}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Closing Statement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        viewport={{ once: true }}
        className="text-center mt-16 px-2"
      >
        <h2 className="text-xl sm:text-2xl font-[DM Sans] font-semibold mb-3">
          We build with purpose. We build with power.
        </h2>
        <p className="text-gray-500 dark:text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
          Every product we develop is designed to simplify complexity, enhance productivity, and bring bold ideas to life.
        </p>
      </motion.div>
    </section>
  );
};

export default About;
